// created by Garrett Lang on 1/31/2020

// usage:
// import MomentDate from '../../utils/dateFormatter';
// import moment from 'moment';
// let now = moment();
// console.log(new MomentDate().toUtcISOString(now));
// let losAngeles = new MomentDate().asTimeZone(now, "America/Los_Angeles");
// console.log(losAngeles);
// let losAngelesUtc = new MomentDate().asTimeZoneUtcISOString(now, "America/Los_Angeles");
// console.log(losAngelesUtc);
// console.log(new MomentDate().fromUtcToTimeZone(losAngelesUtc, "America/Los_Angeles"));
// console.log(new MomentDate().fromUtcToDeviceTimeZone(losAngelesUtc));

import { Timestamp } from 'firebase/firestore';
import { isPlainObject, orderBy } from 'lodash';
import moment from 'moment-timezone';
import logger from './logger';
import { isNotNullOrUndefined, isNullOrUndefined, isStringNotEmpty } from './objectUtils';
import { toFormattedMinutesCountdown } from './stringFormatter';

// nothing here throws but formatDueAt, so an error a screen would otherwise never surface is reported here
const logDateError = (functionName, error) => logger.logErrorEvent('DATE_FORMATTER_ERROR', error, `dateFormatter.${functionName} could not read its input`);

// a date-only string names a calendar day rather than an instant, so a converter that reads it as an instant reads
// the start of that day in the zone it works in - read in the machine's zone, it is the day before east of that zone
const DATE_ONLY_FORMATS = ['YYYY-MM-DD', 'MM/DD/YYYY', 'M/D/YYYY', 'M/DD/YYYY', 'MM/D/YYYY', 'MMM D, YYYY', 'MMM DD, YYYY', 'MMMM D, YYYY', 'MMMM DD, YYYY'];
const DATE_ONLY_PATTERN = /^(?:\d{4}-\d{2}-\d{2}|\d{1,2}\/\d{1,2}\/\d{4}|[A-Za-z]{3,}\s\d{1,2},\s\d{4})$/;

// the range a Firestore Timestamp can hold, the years 0001 through 9999
const MIN_TIMESTAMP_SECONDS = -62135596800;
const MAX_TIMESTAMP_SECONDS = 253402300799;

// a callable sends a Date as ISO text with a time and an offset, so a screen that shows other strings as given still
// reads this one as the instant it is
const ISO_INSTANT_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})$/;

const isDateOnlyString = (value) => typeof value === 'string' && DATE_ONLY_PATTERN.test(value.trim());

const isISOInstantString = (value) => typeof value === 'string' && ISO_INSTANT_PATTERN.test(value.trim());

// strict, so an overflow like 13/45/2030 is invalid rather than rolled forward into a real date
const toCalendarDay = (value, timeZoneName) => moment.tz(value.trim(), DATE_ONLY_FORMATS, true, timeZoneName);

const toDateFromTimestampParts = (seconds, nanoseconds) => {
    const isReadable = Number.isInteger(seconds) && seconds >= MIN_TIMESTAMP_SECONDS && seconds <= MAX_TIMESTAMP_SECONDS
        && Number.isInteger(nanoseconds) && nanoseconds >= 0 && nanoseconds < 1000000000;

    if (isReadable) {
        return new Date(seconds * 1000 + nanoseconds / 1000000);
    }

    // a malformed map is a corrupted date, so it is reported though it is answered as unreadable rather than thrown
    logDateError('toDateIfTimestamp', new Error(`unreadable timestamp map: seconds ${String(seconds)}, nanoseconds ${String(nanoseconds)}`));
    return new Date(NaN);
};

// a timestamp arrives as a Timestamp, as { seconds, nanoseconds } off the client sdk, or as { _seconds, _nanoseconds }
// off the admin sdk. maps are read here because the two sdks' constructors reject different malformed maps
const toDateIfTimestamp = (value) => {
    if (value instanceof Timestamp) {
        return value.toDate();
    }

    if (value instanceof Object && 'seconds' in value && 'nanoseconds' in value) {
        return toDateFromTimestampParts(value.seconds, value.nanoseconds);
    }

    if (value instanceof Object && '_seconds' in value && '_nanoseconds' in value) {
        return toDateFromTimestampParts(value._seconds, value._nanoseconds);
    }

    // moment reads any other plain object as a set of units and answers with a date from today
    if (isPlainObject(value)) {
        return new Date(NaN);
    }

    return value;
};

// an unreadable moment formats as "Invalid date", which slips past a caller's fallback, and as a null ISO string,
// where every caller expects a string
const toDisplayText = (dateMoment, displayFormat) => (dateMoment.isValid() ? dateMoment.format(displayFormat) : '');

const toISOText = (dateMoment, keepOffset) => (dateMoment.isValid() ? dateMoment.toISOString(keepOffset) : '');

const toInstant = (value, timeZoneName) => (isDateOnlyString(value) ? toCalendarDay(value, timeZoneName).toDate() : toDateIfTimestamp(value));

// some runtimes cannot name their own zone, and pacific is the business's
const getDeviceTimeZone = () => {
    try {
        const deviceTimeZone = moment.tz.guess();
        return isStringNotEmpty(deviceTimeZone) ? deviceTimeZone : 'US/Pacific';
    } catch (error) {
        return 'US/Pacific';
    }
};

export const toUtcISOString = (value) => {
    if (isNotNullOrUndefined(value)) {
        try {
            return toISOText(moment(toInstant(value, 'US/Pacific')).utc(), true);
        } catch (error) {
            logDateError('toUtcISOString', error);
            return '';
        }
    }

    return '';
};

export const toUtcISOStringWithTimeZone = (value, timeZone) => {
    if (isNotNullOrUndefined(value)) {
        try {
            return toISOText(moment.tz(toDateIfTimestamp(value), timeZone).utc(), true);
        } catch (error) {
            logDateError('toUtcISOStringWithTimeZone', error);
            return '';
        }
    }

    return '';
};

export const toISOStringWithTimeZone = (value, timeZone, keepOffset = false) => {
    if (isNotNullOrUndefined(value)) {
        try {
            return toISOText(moment.tz(toDateIfTimestamp(value), timeZone), keepOffset);
        } catch (error) {
            logDateError('toISOStringWithTimeZone', error);
            return '';
        }
    }

    return '';
};

// sets timezone-less moment datetime to be that datetime in a specified timezone
export const asTimeZone = (value, timeZoneName) => {
    var localFormat = 'YYYY-MM-DD[T]HH:mm:ss';
    if (isNotNullOrUndefined(value) && isStringNotEmpty(timeZoneName)) {
        try {
            var datetime = moment(toDateIfTimestamp(value)).format(localFormat);
            return moment.tz(datetime, timeZoneName);
        } catch (error) {
            logDateError('asTimeZone', error);
            return moment.invalid();
        }
    }

    return null;
};

// sets timezone-less moment time to have the date of another datetime and then the result to be a datetime in a specified timezone
export const combineDateAndTimeAsTimeZone = (date, time, timeZoneName) => {
    var dateFormat = 'YYYY-MM-DD';
    var timeFormat = 'HH:mm';
    //var dateTimeFormat = 'YYYY-MM-DD[T]HH:mm:ss';

    if (isNotNullOrUndefined(date) && isNotNullOrUndefined(time) && isStringNotEmpty(timeZoneName)) {
        try {
            // console.log(timeZoneName);
            // console.log(moment(date).format(dateFormat));
            // console.log(moment(time).format(timeFormat));
            var formattedDateString = moment(toDateIfTimestamp(date)).format(dateFormat);
            var formattedTimeString = moment(toDateIfTimestamp(time)).format(timeFormat);
            var formattedDateTimeString = `${formattedDateString}T${formattedTimeString}`;
            // console.log(moment(formattedDateTimeString).format(dateTimeFormat));
            // console.log(moment.tz(formattedDateTimeString, timeZoneName));
            // console.log(moment.tz(formattedDateTimeString, timeZoneName).utc().toISOString(true));
            return moment.tz(formattedDateTimeString, timeZoneName);
        } catch (error) {
            logDateError('combineDateAndTimeAsTimeZone', error);
            return moment.invalid();
        }
    }

    return null;
};

// sets timezone-less moment time to have the date of another datetime and then the result to be a datetime in a specified timezone and returns it as a utc iso string
export const combineDateAndTimeAsTimeZoneUtcISOString = (date, time, timeZoneName) => {
    if (isNotNullOrUndefined(date) && isNotNullOrUndefined(time) && isStringNotEmpty(timeZoneName)) {
        try {
            return toISOText(combineDateAndTimeAsTimeZone(date, time, timeZoneName).utc(), true);
        } catch (error) {
            logDateError('combineDateAndTimeAsTimeZoneUtcISOString', error);
            return '';
        }
    }

    return '';
};

// sets timezone-less moment time to have the date of another datetime and then the result to be a datetime in a specified timezone and returns it as a utc date
export const combineDateAndTimeAsTimeZoneUtcDate = (date, time, timeZoneName) => {
    if (isNotNullOrUndefined(date) && isNotNullOrUndefined(time) && isStringNotEmpty(timeZoneName)) {
        try {
            return combineDateAndTimeAsTimeZone(date, time, timeZoneName).utc().toDate();
        } catch (error) {
            logDateError('combineDateAndTimeAsTimeZoneUtcDate', error);
            return new Date(NaN);
        }
    }

    return null;
};

// sets timezone-less moment datetime to be that datetime in a specified timezone and returns it as a utc iso string
export const asTimeZoneUtcISOString = (value, timeZoneName) => {
    if (isNotNullOrUndefined(value) && isStringNotEmpty(timeZoneName)) {
        try {
            return toISOText(asTimeZone(value, timeZoneName).utc(), true);
        } catch (error) {
            logDateError('asTimeZoneUtcISOString', error);
            return '';
        }
    }

    return '';
};

export const fromUtcToDeviceTimeZoneAsMoment = (value) => {
    if (isNotNullOrUndefined(value)) {
        try {
            return moment.utc(toDateIfTimestamp(value)).tz(getDeviceTimeZone());
        } catch (error) {
            logDateError('fromUtcToDeviceTimeZoneAsMoment', error);
            return moment.invalid();
        }
    }

    return null;
};

// Convert DateTime from Backend (utc time) to a specific TimeZone and return it as a string with format MM/DD/YYYY HH:mm z
export const fromUtcToTimeZone = (value, timeZoneName, displayFormat = 'MM/DD/YYYY HH:mm z') => {
    if (isNotNullOrUndefined(value) && isStringNotEmpty(timeZoneName)) {
        try {
            return toDisplayText(moment.utc(toDateIfTimestamp(value)).tz(timeZoneName), displayFormat);
        } catch (error) {
            logDateError('fromUtcToTimeZone', error);
            return '';
        }
    }

    return '';
};

// Convert DateTime from Backend (utc time) to a specific TimeZone and return it as a moment date as local timezone (to be used in edit forms)
export const fromUtcToTimeZoneAsTimeZone = (value, timeZoneName) => {
    var localFormat = 'YYYY-MM-DD[T]HH:mm:ss';
    if (isNotNullOrUndefined(value) && isStringNotEmpty(timeZoneName)) {
        try {
            var timeZonedDateTime = moment.utc(toDateIfTimestamp(value)).tz(timeZoneName).format(localFormat);
            return moment.tz(timeZonedDateTime, getDeviceTimeZone());
        } catch (error) {
            logDateError('fromUtcToTimeZoneAsTimeZone', error);
            return moment.invalid();
        }
    }

    return null;
};

// Convert DateTime from Backend (utc time) to a specific TimeZone and return it as a string with format MM/DD/YYYY
export const fromUtcToTimeZoneDateOnly = (value, timeZoneName) => {
    var displayFormat = 'MM/DD/YYYY';
    if (isNotNullOrUndefined(value) && isStringNotEmpty(timeZoneName)) {
        try {
            return toDisplayText(moment.utc(toDateIfTimestamp(value)).tz(timeZoneName), displayFormat);
        } catch (error) {
            logDateError('fromUtcToTimeZoneDateOnly', error);
            return '';
        }
    }

    return '';
};

// Convert DateTime from Backend (utc time) to a specific TimeZone and return it as a string with format HH:mm z
export const fromUtcToTimeZoneTimeOnly = (value, timeZoneName) => {
    var displayFormat = 'HH:mm z';
    if (isNotNullOrUndefined(value) && isStringNotEmpty(timeZoneName)) {
        try {
            return toDisplayText(moment.utc(toDateIfTimestamp(value)).tz(timeZoneName), displayFormat);
        } catch (error) {
            logDateError('fromUtcToTimeZoneTimeOnly', error);
            return '';
        }
    }

    return '';
};

// Convert DateTime from Backend (utc time) to the TimeZone of Browser/Device and return it as a string with format MM/DD/YYYY HH:mm z
export const fromUtcToDeviceTimeZone = (value, displayFormat = 'MM/DD/YYYY HH:mm z') => {
    if (isNotNullOrUndefined(value)) {
        try {
            return toDisplayText(moment.utc(toDateIfTimestamp(value)).tz(getDeviceTimeZone()), displayFormat);
        } catch (error) {
            logDateError('fromUtcToDeviceTimeZone', error);
            return '';
        }
    }

    return '';
};

// Convert DateTime from Backend (utc time) to the TimeZone of Browser/Device and return it as a string with format MM/DD/YYYY
export const fromUtcToDeviceTimeZoneDateOnly = (value, displayFormat = 'MM/DD/YYYY') => {
    if (isNotNullOrUndefined(value)) {
        try {
            if (isISOInstantString(value)) {
                return toDisplayText(moment.utc(value.trim()).tz(getDeviceTimeZone()), displayFormat);
            }
            if (typeof value === 'string') {
                // console.log('isString');
                // console.log(value);
                return value;
            }
            if (value instanceof String) {
                // console.log('isString');
                // console.log(value);
                return value;
            }

            // console.log('isTimestamp')
            // return moment.utc(value).format(displayFormat);
            return toDisplayText(moment.utc(toDateIfTimestamp(value)).tz(getDeviceTimeZone()), displayFormat);
        } catch (error) {
            logDateError('fromUtcToDeviceTimeZoneDateOnly', error);
            return '';
        }
    }

    return '';
};

// Convert DateTime from Backend (utc time) to the TimeZone of Browser/Device and return it as a string with format HH:mm z
export const fromUtcToDeviceTimeZoneTimeOnly = (value, displayFormat = 'HH:mm z') => {
    if (isNotNullOrUndefined(value)) {
        try {
            return toDisplayText(moment.utc(toDateIfTimestamp(value)).tz(getDeviceTimeZone()), displayFormat);
        } catch (error) {
            logDateError('fromUtcToDeviceTimeZoneTimeOnly', error);
            return '';
        }
    }

    return '';
};

// Convert DateTime from Backend (utc time) to the TimeZone of Browser/Device and return it as a string with format HH:mm z | MM/DD/YYYY
export const fromUtcToDeviceTimeZoneForMessages = (value, displayFormat = 'HH:mm z | MM/DD/YYYY') => {
    if (isNotNullOrUndefined(value)) {
        try {
            return toDisplayText(moment.utc(toDateIfTimestamp(value)).tz(getDeviceTimeZone()), displayFormat);
        } catch (error) {
            logDateError('fromUtcToDeviceTimeZoneForMessages', error);
            return '';
        }
    }

    return '';
};

export const fromUtcToDeviceTimeZoneForEncounters = (value, displayFormat = 'MM/DD/YYYY HH:mm z') => {
    if (isNotNullOrUndefined(value)) {
        try {
            return toDisplayText(moment.utc(toDateIfTimestamp(value)).tz(getDeviceTimeZone()), displayFormat);
        } catch (error) {
            logDateError('fromUtcToDeviceTimeZoneForEncounters', error);
            return '';
        }
    }

    return '';
};

// takes in a date of birth (from database) and converts to an age
export const toAge = (value) => {
    if (isNotNullOrUndefined(value)) {
        try {
            return moment().diff(moment(new Date(toDateIfTimestamp(value))), 'years').toString();
        } catch (error) {
            logDateError('toAge', error);
            return '';
        }
    }

    return '';
};

export const toAgeAsNumber = (value) => {
    if (isNotNullOrUndefined(value)) {
        try {
            return moment().diff(moment(new Date(toDateIfTimestamp(value))), 'years');
        } catch (error) {
            logDateError('toAgeAsNumber', error);
            return NaN;
        }
    }

    return null;
};

export const convertToMomentUtc = (value) => {
    if (isNotNullOrUndefined(value)) {
        try {
            if (typeof value === 'string') {
                return moment.utc(value);
            }
            return moment.utc(toDateIfTimestamp(value));
        } catch (error) {
            logDateError('convertToMomentUtc', error);
            return moment.invalid();
        }
    }

    return null;
};

export const convertToMoment = (value) => {
    if (isNotNullOrUndefined(value)) {
        try {
            if (typeof value === 'string') {
                return moment(value);
            }
            return moment(toDateIfTimestamp(value));
        } catch (error) {
            logDateError('convertToMoment', error);
            return moment.invalid();
        }
    }

    return null;
};

export const convertToDateUtc = (value) => {
    if (isNotNullOrUndefined(value)) {
        try {
            if (typeof value === 'string') {
                return moment.utc(value).toDate();
            }
            return moment.utc(toDateIfTimestamp(value)).toDate();
        } catch (error) {
            logDateError('convertToDateUtc', error);
            return new Date(NaN);
        }
    }

    return null;
};

export const convertToDate = (value) => {
    if (isNotNullOrUndefined(value)) {
        try {
            return moment(toInstant(value, 'US/Pacific')).toDate();
        } catch (error) {
            logDateError('convertToDate', error);
            return new Date(NaN);
        }
    }

    return null;
};

/**
 * Normalizes Firestore Timestamps, Dates, {seconds} objects, and date-like strings into milliseconds.
 * Returns 0 for nullish/invalid input.
 *
 * @param {*} value - Date-like value to normalize.
 * @returns {number}
 */
export const toMillis = (value) => {
    if (isNullOrUndefined(value)) return 0;

    try {
        if (typeof value?.toMillis === 'function') return value.toMillis();
        if (typeof value?.toDate === 'function') return value.toDate().getTime();
        if (value instanceof Date) return value.getTime();
        if (typeof value === 'object' && isNotNullOrUndefined(value.seconds)) return value.seconds * 1000;
        // the admin sdk holds seconds on an underscored own field, so a timestamp it stamped crosses a callable as a
        // plain object this would otherwise read as epoch
        if (typeof value === 'object' && isNotNullOrUndefined(value._seconds)) return value._seconds * 1000;
        const parsed = new Date(value).getTime();
        return Number.isFinite(parsed) ? parsed : 0;
    } catch (error) {
        logDateError('toMillis', error);
        return 0;
    }
};

export const fromUtcToStartDate = (value, timeZoneName = 'US/Pacific') => {
    if (isNotNullOrUndefined(value) && isStringNotEmpty(timeZoneName)) {
        try {
            return moment(toInstant(value, timeZoneName)).tz(timeZoneName).startOf('day').utc().toDate();
        } catch (error) {
            logDateError('fromUtcToStartDate', error);
            return new Date(NaN);
        }
    }

    return null;
};

export const fromUtcToStartDateString = (value, timeZoneName = 'US/Pacific') => {
    if (isNotNullOrUndefined(value) && isStringNotEmpty(timeZoneName)) {
        try {
            const startOfDay = moment(toInstant(value, timeZoneName)).tz(timeZoneName).startOf('day').utc();
            return startOfDay.isValid() ? startOfDay.format('MM/DD/YYYY') : null;
        } catch (error) {
            logDateError('fromUtcToStartDateString', error);
            return null;
        }
    }

    return null;
};

/**
 * Formats a date-like value as a Pacific MM/DD/YYYY start-of-day string for display,
 * returning `emptyLabel` for empty or unreadable values. Set `passthroughStrings` to return
 * already-formatted string inputs unchanged; ISO text with a time and an offset is still read as a date.
 *
 * @param {*} value - Date-like value or display string.
 * @param {object} [options]
 * @param {boolean} [options.passthroughStrings=false] - Return string inputs other than ISO instants verbatim.
 * @param {string} [options.emptyLabel='—'] - Value returned for empty input.
 * @returns {string}
 */
export const toPacificStartDateDisplay = (value, options) => {
    const { passthroughStrings = false, emptyLabel = '—' } = options ?? {};
    if (value === null || value === undefined || value === '') return emptyLabel;
    if (passthroughStrings && typeof value === 'string' && !isISOInstantString(value)) return value;
    return fromUtcToStartDateString(typeof value === 'string' ? value.trim() : value, 'US/Pacific') ?? emptyLabel;
};

export const fromUtcToLongDateString = (value, timeZoneName = 'US/Pacific') => {
    if (isNotNullOrUndefined(value) && isStringNotEmpty(timeZoneName)) {
        try {
            if (isDateOnlyString(value)) {
                const calendarDate = toCalendarDay(value, timeZoneName);
                return calendarDate.isValid() ? calendarDate.format('MMM D, YYYY') : value;
            }

            const parsed = moment(toDateIfTimestamp(value));
            return parsed.isValid() ? parsed.tz(timeZoneName).format('MMM D, YYYY') : null;
        } catch (error) {
            logDateError('fromUtcToLongDateString', error);
            return null;
        }
    }

    return null;
};

export const fromUtcToStartTimeString = (value, timeZoneName = 'US/Pacific') => {
    if (isNotNullOrUndefined(value) && isStringNotEmpty(timeZoneName)) {
        try {
            const startTime = moment(toInstant(value, timeZoneName)).tz(timeZoneName);
            return startTime.isValid() ? startTime.format('HH:mm') : null;
        } catch (error) {
            logDateError('fromUtcToStartTimeString', error);
            return null;
        }
    }

    return null;
};

export const fromUtcToStartDateMoment = (value, timeZoneName = 'US/Pacific') => {
    if (isNotNullOrUndefined(value) && isStringNotEmpty(timeZoneName)) {
        try {
            return moment(toInstant(value, timeZoneName)).tz(timeZoneName).startOf('day').utc();
        } catch (error) {
            logDateError('fromUtcToStartDateMoment', error);
            return moment.invalid();
        }
    }

    return null;
};

export const fromUtcToDate = (value, timeZoneName = 'US/Pacific') => {
    if (isNotNullOrUndefined(value) && isStringNotEmpty(timeZoneName)) {
        try {
            const convertedValue = moment(toInstant(value, timeZoneName)).tz(timeZoneName);

            // an unparsable value otherwise answers with an Invalid Date, which passes every
            // null check it meets and is rejected by the sdk only at the write
            return convertedValue.isValid() ? convertedValue.utc().toDate() : null;
        } catch (error) {
            logDateError('fromUtcToDate', error);
            return null;
        }
    }

    return null;
};

export const fromUtcToMoment = (value, timeZoneName = 'US/Pacific') => {
    if (isNotNullOrUndefined(value) && isStringNotEmpty(timeZoneName)) {
        try {
            return moment(toInstant(value, timeZoneName)).tz(timeZoneName).utc();
        } catch (error) {
            logDateError('fromUtcToMoment', error);
            return moment.invalid();
        }
    }

    return null;
};

export const toDaysAgo = (value, timeZoneName = 'US/Pacific') => {
    if (isNotNullOrUndefined(value) && isStringNotEmpty(timeZoneName)) {
        try {
            const momentDate = moment(toInstant(value, timeZoneName)).tz(timeZoneName).utc();
            if (momentDate.isValid() === false) {
                return '';
            }

            let minutesPassed = moment().tz(timeZoneName).utc().diff(momentDate, 'minutes');
            return toFormattedMinutesCountdown(minutesPassed);
        } catch (error) {
            logDateError('toDaysAgo', error);
            return '';
        }
    }

    return '';
};

// Function to get event length in minutes
export const eventMinutes = (startDate, endDate) => {
    const start = new Date(fromUtcToDeviceTimeZone(startDate));
    const end = new Date(fromUtcToDeviceTimeZone(endDate));

    // Calculate the difference in milliseconds and convert to minutes
    const diffInMilliseconds = end - start;
    const diffInMinutes = Math.floor(diffInMilliseconds / (1000 * 60));

    return diffInMinutes;
};

// With suffix (default)
// formatDistanceToNow(new Date()); // "just now"
// formatDistanceToNow(new Date(Date.now() - 5 * 60 * 1000)); // "5 minutes ago"
// formatDistanceToNow(new Date(Date.now() - 24 * 60 * 60 * 1000)); // "1 day ago"

// Without suffix
// formatDistanceToNow(new Date(), { addSuffix: false }); // "less than a minute"
export const formatDistanceToNow = (value, options = { addSuffix: true }) => {
    if (isNullOrUndefined(value)) {
        return '';
    }

    try {
        // Handle different date input types
        let dateToFormat = toDateIfTimestamp(value);
        if (dateToFormat instanceof Date === false) {
            dateToFormat = new Date(value);
        }

        if (Number.isNaN(dateToFormat.getTime())) {
            return '';
        }

        const now = new Date();
        const diffInSeconds = Math.floor((now - dateToFormat) / 1000);

        // Less than 1 minute
        if (diffInSeconds < 60) {
            return options.addSuffix ? 'just now' : 'less than a minute';
        }

        // Less than 1 hour
        if (diffInSeconds < 3600) {
            const minutes = Math.floor(diffInSeconds / 60);
            return `${minutes} minute${minutes !== 1 ? 's' : ''} ${options.addSuffix ? 'ago' : ''}`;
        }

        // Less than 1 day
        if (diffInSeconds < 86400) {
            const hours = Math.floor(diffInSeconds / 3600);
            return `${hours} hour${hours !== 1 ? 's' : ''} ${options.addSuffix ? 'ago' : ''}`;
        }

        // Less than 1 week
        if (diffInSeconds < 604800) {
            const days = Math.floor(diffInSeconds / 86400);
            return `${days} day${days !== 1 ? 's' : ''} ${options.addSuffix ? 'ago' : ''}`;
        }

        // Less than 1 month
        if (diffInSeconds < 2592000) {
            const weeks = Math.floor(diffInSeconds / 604800);
            return `${weeks} week${weeks !== 1 ? 's' : ''} ${options.addSuffix ? 'ago' : ''}`;
        }

        // Less than 1 year
        if (diffInSeconds < 31536000) {
            const months = Math.floor(diffInSeconds / 2592000);
            return `${months} month${months !== 1 ? 's' : ''} ${options.addSuffix ? 'ago' : ''}`;
        }

        // More than 1 year
        const years = Math.floor(diffInSeconds / 31536000);
        return `${years} year${years !== 1 ? 's' : ''} ${options.addSuffix ? 'ago' : ''}`;

    } catch (error) {
        logDateError('formatDistanceToNow', error);
        return '';
    }
};

// keep the original when the value will not convert: the consult pages filter on submittedAt !== null, so a shape
// nothing recognises would otherwise drop a submitted form out of the clinical record
export const toSubmittedAtDate = (value) => {
    const submittedAt = fromUtcToDate(value);

    return isNotNullOrUndefined(submittedAt) && Number.isNaN(submittedAt.getTime()) === false ? submittedAt : value;
};

// a submittedAt embedded in a parent document arrives from three different writers - a Firestore
// Timestamp, a callable-rasterised { seconds, nanoseconds } map, and an ISO string - and lodash
// compares two of those maps as "[object Object]", so the raw field cannot be ordered directly
export const orderBySubmittedAt = (records, direction = 'desc') => {
    try {
        return orderBy(records, [(record) => fromUtcToDate(record?.submittedAt)], [direction]);
    } catch (error) {
        logDateError('orderBySubmittedAt', error);
        return [];
    }
};

// the one function here that throws: a due date with no timestamp stops Task.create before its batch commits
export const formatDueAt = (dueAt) => {
    if (!dueAt) return '';

    const m = fromUtcToTimeZoneAsTimeZone(dueAt?.timestamp, 'US/Pacific');
    if (m.isValid() === false) return '';

    const date = m.format('MM/DD/YYYY');
    return dueAt.time ? `${date} at ${moment(dueAt.time, 'HH:mm').format('h:mm A z')}` : date;
};
